/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). The two businesses the
 * Replenishment page serves: Distribution (the warehouses that resell to
 * customers) and Manufacturing (the panel shop that builds panels and kits).
 * A site's kind decides which side a cart or an open-demand row belongs to.
 */
import { getSite, SITE_LIST } from '@/demo/procurement/data/company';
import type { Site, SiteId } from '@/demo/procurement/data/types';

export type Business = 'distribution' | 'manufacturing';

/** What the page shows: both businesses or one of them. */
export type BusinessMode = 'all' | Business;

export const BUSINESS_MODES: BusinessMode[] = [
  'all',
  'distribution',
  'manufacturing',
];

export const BUSINESS_MODE_LABEL: Record<BusinessMode, string> = {
  all: 'All',
  distribution: 'Distribution',
  manufacturing: 'Manufacturing',
};

export function isBusinessMode(value: unknown): value is BusinessMode {
  return (
    value === 'all' || value === 'distribution' || value === 'manufacturing'
  );
}

/** Which business a site serves; offices serve neither. */
export function siteBusiness(siteId: SiteId): Business | null {
  const kind = getSite(siteId).kind;
  if (kind === 'dc') return 'distribution';
  if (kind === 'plant') return 'manufacturing';
  return null;
}

/** Whether a site is on the page in this mode. */
export function siteInMode(siteId: SiteId, mode: BusinessMode): boolean {
  return mode === 'all' || siteBusiness(siteId) === mode;
}

/** The business a warehouse name belongs to (open demand names its site in words). */
export function businessOfSiteName(name: string): Business | null {
  const site = SITE_LIST.find((entry) => entry.name === name);
  return site === undefined ? null : siteBusiness(site.id);
}

function sitesOf(business: Business): Site[] {
  return SITE_LIST.filter((site) => siteBusiness(site.id) === business);
}

function joinNames(sites: readonly Site[]): string {
  const names = sites.map((site) => site.city);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** One plain sentence on what the mode covers, from the sites in the cast. */
export function describeMode(mode: BusinessMode): string {
  const warehouses = sitesOf('distribution');
  const plants = sitesOf('manufacturing');
  const plantNames = plants.map((site) => site.name).join(' and ');
  switch (mode) {
    case 'distribution':
      return `The ${joinNames(warehouses)} warehouses: customer back orders, special orders and stock to resell.`;
    case 'manufacturing':
      return `The ${plantNames}: parts kept on hand for the panel and kit builds planned over the next six months.`;
    default:
      return `Both sides of the business: the ${warehouses.length} warehouses that sell to customers, and the ${plantNames} that builds panels and kits.`;
  }
}
